import { Bars3Icon, ChevronDoubleLeftIcon } from '@heroicons/react/24/outline';
import { useEffect, useState } from 'react';
import { Link, useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { DocumentationMarkdownContent } from '../../components/editor/DocumentationMarkdownContent';
import {
  DocumentationPageTreeNode,
  fetchPublicDocumentationPage,
  fetchPublicDocumentationSpace,
  PublicDocumentationPage,
  PublicDocumentationSpace,
} from '../../services/documentationApi';
import './documentationWiki.css';

const PUBLIC_TREE_COLLAPSED_KEY = 'public.documentation.tree-collapsed';
const PUBLIC_TREE_WIDTH = 260;
const PUBLIC_TREE_COLLAPSED_WIDTH = 52;
const BARUK_SITE_URL = 'https://baruk.ai';
const BARUK_CONTACT_EMAIL = 'falecom@baruk.ai';

/** Query flag: preview do editor — não contabiliza visualização. */
export const LIVE_PREVIEW_QUERY = 'countView';

function isMarkdownFormat(format?: string | null): boolean {
  return (format ?? '').toLowerCase() === 'markdown';
}

function readTreeCollapsed(): boolean {
  try {
    return window.localStorage.getItem(PUBLIC_TREE_COLLAPSED_KEY) === '1';
  } catch {
    return false;
  }
}

function publicPagePath(token: string, slug: string, countView: boolean): string {
  const base = `/docs/${token}/${slug}`;
  return countView ? base : `${base}?${LIVE_PREVIEW_QUERY}=false`;
}

function TreeList({
  nodes,
  token,
  activeSlug,
  countView,
  depth = 0,
}: {
  nodes: DocumentationPageTreeNode[];
  token: string;
  activeSlug?: string;
  countView: boolean;
  depth?: number;
}) {
  return (
    <ul className={depth === 0 ? 'docs-nav-tree' : 'docs-nav-tree docs-nav-tree--nested'}>
      {nodes.map((node) => {
        const isActive = Boolean(activeSlug) && activeSlug === node.slug;
        return (
          <li key={node.slug}>
            <div
              className={`docs-nav-row${isActive ? ' is-active' : ''}`}
              style={{ paddingLeft: `${0.35 + depth * 0.75}rem` }}
            >
              <Link
                to={publicPagePath(token, node.slug, countView)}
                className={`docs-nav-link${isActive ? ' is-active' : ''}`}
                aria-current={isActive ? 'page' : undefined}
              >
                <span className="docs-nav-link__title">{node.title}</span>
              </Link>
            </div>
            {node.children?.length ? (
              <TreeList
                nodes={node.children}
                token={token}
                activeSlug={activeSlug}
                countView={countView}
                depth={depth + 1}
              />
            ) : null}
          </li>
        );
      })}
    </ul>
  );
}

function firstSlug(nodes: DocumentationPageTreeNode[]): string | undefined {
  for (const node of nodes) {
    if (node.slug) return node.slug;
    const child = firstSlug(node.children ?? []);
    if (child) return child;
  }
  return undefined;
}

export function PublicDocumentationPageView() {
  const { token = '', pageSlug } = useParams();
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const countView = searchParams.get(LIVE_PREVIEW_QUERY) !== 'false';
  const [space, setSpace] = useState<PublicDocumentationSpace | null>(null);
  const [page, setPage] = useState<PublicDocumentationPage | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [treeCollapsed, setTreeCollapsed] = useState(readTreeCollapsed);

  function toggleTreeCollapsed(): void {
    setTreeCollapsed((prev) => {
      const next = !prev;
      try {
        window.localStorage.setItem(PUBLIC_TREE_COLLAPSED_KEY, next ? '1' : '0');
      } catch {
        // ignore
      }
      return next;
    });
  }

  useEffect(() => {
    void (async () => {
      setLoading(true);
      setError(null);
      try {
        const spaceData = await fetchPublicDocumentationSpace(token);
        setSpace(spaceData);

        const slug = pageSlug || firstSlug(spaceData.pages);
        if (!pageSlug && slug) {
          navigate(publicPagePath(token, slug, countView), { replace: true });
          return;
        }
        if (slug) {
          const pageData = await fetchPublicDocumentationPage(token, slug, { countView });
          setPage(pageData);
        } else {
          setPage(null);
        }
      } catch {
        setError('Documentação não encontrada ou indisponível.');
        setSpace(null);
        setPage(null);
      } finally {
        setLoading(false);
      }
    })();
  }, [token, pageSlug, navigate, countView]);

  if (loading) {
    return (
      <div className="docs-public-shell">
        <p className="p-8 text-secondary-600">Carregando...</p>
      </div>
    );
  }

  if (error || !space) {
    return (
      <div className="docs-public-shell">
        <div className="p-8 text-center">
          <h1 className="text-xl font-semibold">Documentação indisponível</h1>
          <p className="mt-2 text-secondary-600">{error}</p>
        </div>
      </div>
    );
  }

  return (
    <div className="docs-public-shell">
      <header className="docs-public-header">
        <strong>{space.name}</strong>
        {space.description ? <span className="text-secondary-500 text-sm">{space.description}</span> : null}
      </header>
      <div
        className={`docs-wiki-layout docs-wiki-layout--public${
          treeCollapsed ? ' docs-wiki-layout--tree-collapsed' : ''
        }`}
        style={{
          gridTemplateColumns: `${treeCollapsed ? PUBLIC_TREE_COLLAPSED_WIDTH : PUBLIC_TREE_WIDTH}px minmax(0, 1fr)`,
        }}
      >
        <aside className="docs-wiki-sidebar">
          {treeCollapsed ? (
            <div className="docs-wiki-sidebar__collapsed">
              <button
                type="button"
                className="docs-wiki-sidebar__expand-btn"
                title="Expandir sumário"
                aria-label="Expandir sumário"
                onClick={toggleTreeCollapsed}
              >
                <Bars3Icon className="h-5 w-5" />
              </button>
            </div>
          ) : (
            <>
              <div className="docs-wiki-sidebar__header">
                <div className="docs-wiki-sidebar__header-row">
                  <span className="docs-wiki-sidebar__title">Sumário</span>
                  <button
                    type="button"
                    className="docs-icon-btn"
                    title="Recolher sumário"
                    aria-label="Recolher sumário"
                    onClick={toggleTreeCollapsed}
                  >
                    <ChevronDoubleLeftIcon className="h-4 w-4" />
                  </button>
                </div>
              </div>
              <div className="docs-wiki-sidebar__body">
                <TreeList
                  nodes={space.pages}
                  token={token}
                  activeSlug={page?.slug ?? pageSlug}
                  countView={countView}
                />
              </div>
              <footer className="docs-wiki-sidebar__footer">
                <p>© {new Date().getFullYear()} Baruk. Todos os direitos reservados.</p>
                <p>
                  <a href={`mailto:${BARUK_CONTACT_EMAIL}`}>{BARUK_CONTACT_EMAIL}</a>
                </p>
                <p>
                  <a href={BARUK_SITE_URL} target="_blank" rel="noopener noreferrer">
                    baruk.ai
                  </a>
                </p>
              </footer>
            </>
          )}
        </aside>
        <main className="docs-wiki-main">
          <div className="docs-wiki-main__scroll">
            {!page ? (
              <p className="erp-page-description">Nenhuma página publicada neste documento.</p>
            ) : (
              <>
                <article className="docs-wiki-article">
                  <h1 className="docs-wiki-article__title">{page.title}</h1>
                  {isMarkdownFormat(page.contentFormat) ? (
                    <div className="docs-wiki-prose">
                      <DocumentationMarkdownContent value={page.body} emptyLabel="Sem conteúdo." />
                    </div>
                  ) : (
                    <div
                      className="docs-wiki-prose"
                      dangerouslySetInnerHTML={{
                        __html: page.body || '<p><em>Sem conteúdo.</em></p>',
                      }}
                    />
                  )}
                </article>
                <nav className="docs-passadores">
                  {page.previousPage ? (
                    <Link
                      className="docs-passador docs-passador--prev"
                      to={publicPagePath(token, page.previousPage.slug, countView)}
                    >
                      ← {page.previousPage.title}
                    </Link>
                  ) : (
                    <span />
                  )}
                  {page.nextPage ? (
                    <Link
                      className="docs-passador docs-passador--next"
                      to={publicPagePath(token, page.nextPage.slug, countView)}
                    >
                      {page.nextPage.title} →
                    </Link>
                  ) : null}
                </nav>
              </>
            )}
          </div>
        </main>
      </div>
    </div>
  );
}
